<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { formatTime } from '@/utils/datetime'
import { User, Bot, Loader2 } from 'lucide-vue-next'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })

const props = defineProps({
  messages: {
    type: Array,
    default: () => []
  },
  isGenerating: {
    type: Boolean,
    default: false
  },
  isStreaming: {
    type: Boolean,
    default: false
  },
  streamingMessage: {
    type: String,
    default: ''
  }
})

// Sort messages by created_at to ensure correct ordering
const sortedMessages = computed(() => {
  return [...props.messages].sort((a, b) => {
    return new Date(a.created_at) - new Date(b.created_at)
  })
})

const messagesEndRef = ref(null)

// Auto-scroll to bottom
const scrollToBottom = async () => {
  await nextTick()
  if (messagesEndRef.value) {
    messagesEndRef.value.scrollIntoView({ behavior: 'smooth' })
  }
}

// Watch messages and scroll
watch(() => sortedMessages.value, () => {
  scrollToBottom()
}, { deep: true })

// Watch streaming message and scroll
watch(() => props.streamingMessage, () => {
  if (props.isStreaming) {
    scrollToBottom()
  }
})

onMounted(() => {
  scrollToBottom()
})
</script>

<template>
  <div class="p-4 space-y-4">
    <!-- Empty State -->
    <div v-if="sortedMessages.length === 0" class="flex items-center justify-center h-full text-muted-foreground">
      <div class="text-center">
        <Bot class="h-12 w-12 mx-auto mb-4 opacity-30" />
        <p class="text-sm">Start a conversation by sending a message below</p>
      </div>
    </div>

    <!-- Messages -->
    <template v-for="message in sortedMessages" :key="message.code">
      <!-- User Message -->
      <div v-if="message.role === 'user'" class="flex justify-end">
        <div class="flex items-start gap-2 max-w-[80%]">
          <div class="flex-1">
            <div class="bg-blue-600 text-white rounded-lg px-4 py-2">
              <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
            </div>
            <div class="text-xs text-muted-foreground mt-1 text-right">
              {{ formatTime(message.created_at, settings.tz, 'HH:mm') }}
            </div>
          </div>
          <div class="flex-shrink-0">
            <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <User class="h-4 w-4 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <!-- Assistant Message -->
      <div v-else-if="message.role === 'assistant'" class="flex justify-start">
        <div class="flex items-start gap-2 max-w-[80%]">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
              <Bot class="h-4 w-4 text-purple-600" />
            </div>
          </div>
          <div class="flex-1">
            <div class="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
              <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ message.content || 'Generating response...' }}</p>
            </div>
            <div class="text-xs text-muted-foreground mt-1">
              {{ formatTime(message.created_at, settings.tz, 'HH:mm') }}
              <span v-if="message.estimated_cost" class="ml-2">
                · ${{ parseFloat(message.estimated_cost).toFixed(6) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Streaming Indicator - Shows streaming text in real-time -->
    <div v-if="isStreaming && streamingMessage" class="flex justify-start">
      <div class="flex items-start gap-2 max-w-[80%]">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <Bot class="h-4 w-4 text-purple-600" />
          </div>
        </div>
        <div class="flex-1">
          <div class="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
            <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ streamingMessage }}<span class="inline-block w-1 h-4 bg-purple-600 animate-pulse ml-0.5"></span></p>
          </div>
          <div class="text-xs text-muted-foreground mt-1">
            <Loader2 class="h-3 w-3 inline animate-spin mr-1" />
            Working...
          </div>
        </div>
      </div>
    </div>

    <!-- Generating Indicator - Shows when waiting for stream to start -->
    <div v-else-if="isGenerating" class="flex justify-start">
      <div class="flex items-start gap-2">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <Loader2 class="h-4 w-4 text-purple-600 animate-spin" />
          </div>
        </div>
        <div class="flex-1">
          <div class="bg-white border border-gray-200 rounded-lg px-4 py-2">
            <p class="text-sm text-muted-foreground">Thinking...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll anchor -->
    <div ref="messagesEndRef"></div>
  </div>
</template>
